import axios, { AxiosInstance } from 'axios';
import { ArgumentValidator } from '../../helpers';
import { QueryStringBuilder } from '../helpers';
import { ICopyElectionInstructions, IIElectionCopyReceipt, IElectionStatistics, IPagedList, IPagination, ITinyElection } from '../models';
import { IChangeElectionStateResponse } from '../models/election/change-election-state-response';
import { IEditableElection } from '../models/election/editable-election';
import { IEditableState } from '../models/election/editable-state';
import { IElection } from '../models/election/election';
import { IElectionListRequestQuery } from '../models/election/election-list-request-query';
import { INewElection } from '../models/election/new-election';
import { IValidateStateChangeResponse } from '../models/election/validate-state-change-response';
import { INewElectionSignUp } from '../models/new-election-signup';

export class ElectionClient {

    constructor(private axiosClient: AxiosInstance) {

    }

    public get apiUrl() {
        return `/election`;
    }

    public async get(id: string): Promise<IElection> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        const response = await this.axiosClient.get(
            `${this.apiUrl}/${id}`
        );

        return response.data;
    }

    public async list(query: IElectionListRequestQuery, pagination?: IPagination): Promise<IPagedList<ITinyElection>> {

        try {

            const url = new QueryStringBuilder()
                .withPaginationIfNotEmpty(pagination)
                .build(`${this.apiUrl}/list`);

            const response = await this.axiosClient.post(url, query);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async getStatistics(id: string): Promise<IElectionStatistics> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        const response = await this.axiosClient.get(
            `${this.apiUrl}/${id}/statistics`
        );

        return response.data;
    }

    public async create(election: INewElection): Promise<IElection> {

        ArgumentValidator.notNullOrEmpty(election, `election`);

        try {
            const response = await this.axiosClient.post(
                `${this.apiUrl}`,
                election,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async copy(id: string, instructions: ICopyElectionInstructions): Promise<IIElectionCopyReceipt[]> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(instructions, `instructions`);

        try {
            const response = await this.axiosClient.post(
                `${this.apiUrl}/${id}/copy`,
                instructions,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async update(id: string, election: IEditableElection): Promise<IElection> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(election, `election`);

        try {
            const response = await this.axiosClient.put(
                `${this.apiUrl}/${id}`,
                election,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async delete(id: string): Promise<void> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        const response = await this.axiosClient.delete(
            `${this.apiUrl}/${id}`
        );

        return response.data;
    }

    public async archive(id: string): Promise<void> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        const response = await this.axiosClient.patch(
            `${this.apiUrl}/${id}/archive`
        );

        return response.data;
    }

    public async changeState(id: string, state: IEditableState): Promise<IChangeElectionStateResponse> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(state, `state`);

        try {

            const response = await this.axiosClient.patch(
                `${this.apiUrl}/${id}/state`,
                state
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async validateStateChange(id: string, state: IEditableState): Promise<IValidateStateChangeResponse> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(state, `state`);

        const response = await this.axiosClient.post(
            `${this.apiUrl}/${id}/state`,
            state
        );

        return response.data;
    }

    public async signUp(id: string, signUp: INewElectionSignUp): Promise<void> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(signUp, `state`);

        // New client without base address
        const legacyAxiosClient = axios.create();

        try {
            const response = await legacyAxiosClient.post(
                `/electionsignup/store/${id}`,
                signUp
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }
}
